import {
  ALTERNATE_LANGUAGES,
  ENGLISH_LANGUAGE_CODE,
  PARTNER_PROFILE,
  PARTNERS_NOT_FOUND_ROUTE,
  STUDIO_COMMUNITY_BASE_URL,
} from '@utils/constants';
import { appendQueryToPath } from '@utils/appendQueryToPath';

export type PartnersPathSearchParams = Record<
  string,
  string | string[] | undefined
>;

type BuildPartnersNotFoundPathOptions = {
  basename: string;
  searchParams: PartnersPathSearchParams;
  pathLanguage?: string;
  omitQueryKeys?: string[];
};

function omitParams(
  params: PartnersPathSearchParams,
  keys: string[],
): PartnersPathSearchParams {
  const result = { ...params };
  for (const key of keys) {
    delete result[key];
  }
  return result;
}

export function buildPartnersDirectoryPath(
  basename: string,
  language?: string,
): string {
  const isNonEnglishLocale =
    language &&
    language !== ENGLISH_LANGUAGE_CODE &&
    ALTERNATE_LANGUAGES.includes(language);
  return isNonEnglishLocale
    ? `${basename}/${language}/partners`
    : `${basename}/partners`;
}

export function buildCanonicalPartnersPath(
  basename: string,
  searchParams: PartnersPathSearchParams,
): string {
  return appendQueryToPath(`${basename}/partners`, searchParams);
}

export const PARTNERS_DIRECTORY_PROFILE_ENTRY = 'partners_directory';

export function buildPartnersProfilePath(
  basename: string,
  slug: string,
  pathLanguage?: string | null,
  searchParams?: PartnersPathSearchParams,
): string {
  const directoryPath = buildPartnersDirectoryPath(
    basename,
    pathLanguage ?? undefined,
  );
  return appendQueryToPath(
    `${directoryPath}/${encodeURIComponent(slug)}`,
    searchParams,
  );
}

export function buildPartnersProfileUrl({
  host,
  slug,
  pathLanguage,
  useCommunitySubdomain,
}: {
  host: string;
  slug: string;
  pathLanguage?: string | null;
  useCommunitySubdomain: boolean;
}): string {
  if (useCommunitySubdomain) {
    const path = buildPartnersProfilePath('', slug, pathLanguage);
    return new URL(path, STUDIO_COMMUNITY_BASE_URL).toString();
  }
  return `${host}/${PARTNER_PROFILE}/${encodeURIComponent(slug)}`;
}

export function buildPartnersNotFoundPath({
  basename,
  searchParams,
  pathLanguage,
  omitQueryKeys = [],
}: BuildPartnersNotFoundPathOptions): string {
  const redirectQuery = omitParams(searchParams, omitQueryKeys);
  const isNonEnglishLocale =
    pathLanguage &&
    pathLanguage !== ENGLISH_LANGUAGE_CODE &&
    ALTERNATE_LANGUAGES.includes(pathLanguage);
  const notFoundPath = isNonEnglishLocale
    ? `${basename}/${pathLanguage}/${PARTNERS_NOT_FOUND_ROUTE}`
    : `${basename}/${PARTNERS_NOT_FOUND_ROUTE}`;
  return appendQueryToPath(notFoundPath, redirectQuery);
}
